import { useState } from "react";

import { Button } from "../../components/Button";
import { CustomFoodSheet } from "../../components/CustomFoodSheet";
import { Field, FieldSet } from "../../components/Fields";
import { I } from "../../components/icons";
import { DiscloseRow, NavRow, RowGroup } from "../../components/Rows";
import { customFoodsOf, servingKcal } from "../../domain/customFoods";
import { answersSummary } from "../../domain/eatingQuestions";
import { KCAL_PER_KG_FAT } from "../../domain/deficit";
import { todayStr } from "../../domain/date";
import {
  DIABETES_MAX_DEFICIT_KCAL,
  GLP1_MAX_DEFICIT_KCAL,
  OLDER_MAX_DEFICIT_KCAL,
  ageBand,
  defaultProteinPerKg,
  deficitRule,
  eatingEvidence,
  maxDeficitSetting,
  proteinRule,
  proteinSettingRange,
} from "../../domain/eating";
import { curWeight, dayKcal } from "../../domain/energy";
import {
  DEFAULT_DEFICIT_KCAL,
  DEFAULT_PROTEIN_PER_KG,
  WORK_CREDIT,
  deficitTarget,
  energyBalance,
  proteinTarget,
  totalsOf,
} from "../../domain/intake";
import { dailyTargets, nutrientRows } from "../../domain/nutrition";
import type { CustomFood } from "../../domain/types";
import { useDeviceAnswers, useKaya } from "../../store/kayaStore";
import { useProfileForm } from "./profileForm";
import SettingsPage from "./SettingsPage";

/**
 * How hard the deficit is, how much protein to aim for, whether the food log exists at all — and
 * the foods the user added themselves (ADR-032). Those save through the store the moment they are
 * saved in their sheet, not through this page's form, so Save below never holds them back.
 */
export default function FoodSettings() {
  const { f, set, save, dirty } = useProfileForm();
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();

  const weight = curWeight(S);
  const deficit = deficitTarget(S, undefined, device);
  const weeklyKg = ((deficit * 7) / KCAL_PER_KG_FAT).toFixed(2);
  const targets = dailyTargets(S, undefined, undefined, device);
  const rows = nutrientRows(totalsOf([]), targets);
  const band = ageBand(S);
  const isOlder = band === "60+";
  const dRule = deficitRule(S, undefined, device);
  const pRule = proteinRule(S);
  const range = proteinSettingRange(S);
  const evidence = eatingEvidence(S, undefined, device);
  const bal = energyBalance(S, todayStr(), Math.round(dayKcal(S, todayStr())), undefined, device);

  // What overrides the saved deficit today, said where the number is set.
  const deficitNote =
    dRule.limits.includes("pregnant") || dRule.limits.includes("breastfeeding")
      ? " No deficit applies while pregnant or breastfeeding."
      : dRule.limits.includes("gentle")
        ? " Your last questionnaire answer switched the deficit off on this phone."
        : dRule.limits.includes("diabetes") && !isOlder
        ? ` With diabetes Kaya caps it at ${DIABETES_MAX_DEFICIT_KCAL}.`
        : dRule.limits.includes("glp1") && !isOlder
          ? ` On a GLP-1 or weight-loss medicine Kaya caps it at ${GLP1_MAX_DEFICIT_KCAL}.`
          : "";
  const deficitHint = isOlder
    ? `About ${weeklyKg} kg a week at ${deficit} kcal a day — a direction, not a promise. After 60 Kaya starts gentle and caps this at ${OLDER_MAX_DEFICIT_KCAL}.${deficitNote}`
    : `About ${weeklyKg} kg a week at ${deficit} kcal a day — a direction, not a promise. ${DEFAULT_DEFICIT_KCAL} is the usual start; over a third of Out is flagged.${deficitNote}`;
  const proteinHint =
    pRule.reason === "kidney"
      ? `With kidney disease Kaya keeps protein at ${pRule.perKg} g/kg, about ${proteinTarget(S)} g a day — your doctor sets this.`
      : pRule.reason === "glp1"
        ? `On a GLP-1 or weight-loss medicine Kaya keeps protein at least ${pRule.perKg} g/kg, about ${proteinTarget(S)} g a day, in smaller meals.`
        : isOlder
        ? `About ${proteinTarget(S)} g a day, ${pRule.mealG} g a meal. ${range.min.toFixed(1)}–${range.max.toFixed(1)} suits 60+; ${defaultProteinPerKg(S).toFixed(1)} is the default after 60.`
        : `About ${proteinTarget(S)} g a day at ${weight.toFixed(1)} kg. 1.4–2.0 suits training in a deficit; ${DEFAULT_PROTEIN_PER_KG} is the default.`;
  const budgetWhy = bal
    ? `Out ~${bal.out.toLocaleString()} est. less the ${deficit.toLocaleString()} deficit${bal.heldBack ? `, work counted at ${WORK_CREDIT}` : ""}`
    : "Out minus the deficit";
  const mine = customFoodsOf(S);
  // null: closed; "new": adding; otherwise the food being edited
  const [sheet, setSheet] = useState<CustomFood | "new" | null>(null);

  return (
    <SettingsPage
      title="Food & calories"
      intro="What Kaya takes off Out to set a Budget, and what it aims for on protein."
      onSave={() => void save()}
      dirty={dirty}
    >
      <RowGroup>
        <NavRow
          kind="toggle"
          title="Track what I eat"
          subtitle="Off: the Food tab shows what you burn only."
          checked={f.foodOn}
          onChange={(v) => set("foodOn", v)}
        />
      </RowGroup>

      {f.foodOn ? (
        <>
          <FieldSet legend="Daily deficit">
            <Field
              label="Below Out"
              unit="kcal"
              type="number"
              step="50"
              min="0"
              max={String(maxDeficitSetting(S))}
              value={f.deficitKcal}
              onChange={(v) => set("deficitKcal", v)}
              hint={deficitHint}
            />
          </FieldSet>

          <FieldSet legend="Protein">
            <Field
              label="Per kg of body weight"
              unit="g/kg"
              type="number"
              step="0.1"
              min={String(range.min)}
              max={String(range.max)}
              value={f.proteinPerKg}
              onChange={(v) => set("proteinPerKg", v)}
              hint={proteinHint}
            />
          </FieldSet>

          <div className="you-stack">
            <RowGroup heading="Your answers">
              <NavRow
                icon={I.list}
                title="Eating answers"
                subtitle={answersSummary(S) ?? "Not answered yet — a few questions, all taps"}
                value={S.profile.eating?.at !== undefined ? "Change" : "Start"}
                to="/food/questions?from=you"
              />
            </RowGroup>
            <p className="you-fine">They shape the plan, the notes on Food and the limits below.</p>
          </div>

          <div className="you-stack">
            <RowGroup heading="My foods">
              {mine.map((m) => (
                <NavRow
                  key={m.id}
                  title={m.name}
                  subtitle={`${m.unit}, ${m.g.toLocaleString()} g`}
                  value={`${servingKcal(m).toLocaleString()} kcal`}
                  onClick={() => setSheet(m)}
                />
              ))}
              <NavRow
                kind="action"
                icon={I.plus}
                tone="brand"
                title="A food the list lacks"
                subtitle="From its label, or your own recipe"
                action={
                  <Button variant="ghost" onClick={() => setSheet("new")}>
                    Add a food
                  </Button>
                }
              />
            </RowGroup>
            <p className="you-fine">Your foods save as soon as you add them; Save is for the settings above.</p>
          </div>

          <RowGroup heading={isOlder ? "What a day needs · age 60+" : "What a day needs"}>
            <NavRow
              kind="value"
              title="Budget"
              subtitle={budgetWhy}
              value={targets.kcal !== null ? `~${targets.kcal.toLocaleString()} kcal` : "–"}
            />
            {rows.map((r) => (
              <NavRow
                key={r.key}
                kind="value"
                title={r.label}
                subtitle={r.why}
                value={
                  r.target !== null
                    ? `${r.ceiling ? "under " : ""}${r.target.toLocaleString()} ${r.unit}`
                    : "–"
                }
              />
            ))}
            {/* The long reasons, one tap away — the page itself keeps to one line each. */}
            <DiscloseRow title="How this is worked out">
              <p className="you-fine">
                Worked out from your body numbers and the saved settings above; change the inputs,
                not the targets.
                {targets.kcal === null ? " A dash means Kaya needs your year of birth and sex first." : ""}
              </p>
              {/* One line per rule that changed a target for this person, each naming the rule. */}
              {evidence.map((line) => (
                <p className="you-fine" key={line}>
                  {line}
                </p>
              ))}
              <p className="you-fine">
                Work counts at {WORK_CREDIT} in the budget: people eat back part of what they burn and
                the estimates run high. Energy still shows all of it.
              </p>
              <p className="you-fine">
                The deficit uses the usual {KCAL_PER_KG_FAT.toLocaleString()} kcal per kg, a linear
                rule that overstates the long run because the body adapts.
                {pRule.reason === null
                  ? " Protein sits above the 0.83 g/kg RDA on purpose: that is the floor for avoiding deficiency, not the amount that protects muscle while eating less."
                  : ""}{" "}
                Home-cooked figures vary by roughly a third with oil and portion alone, so read the
                trend rather than any single day.
              </p>
            </DiscloseRow>
          </RowGroup>
        </>
      ) : null}

      <CustomFoodSheet
        open={sheet !== null}
        initial={sheet === "new" ? null : sheet}
        onClose={() => setSheet(null)}
      />
    </SettingsPage>
  );
}
